import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Gift, Copy, Share2, Users, Wallet, ArrowRight, TrendingUp, Banknote, Clock3, CheckCircle2, XCircle, X, Loader2, Pencil, Target, Sparkles, Lock, Check, History, UserPlus, BadgeCheck, Send as SendIcon, Trophy, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getLocalTxCodes } from "@/lib/localHistory";
import api, { formatApiError } from "@/lib/api";
import { formatAmount } from "@/lib/constants";

const PAYOUT_CHANNELS = [
  { value: "airtel_gabon", label: "Airtel Money (Gabon)", placeholder: "Numéro Airtel Money (8 chiffres)" },
  { value: "moov_gabon", label: "Moov Money (Gabon)", placeholder: "Numéro Moov Money" },
  { value: "tmoney_togo", label: "T-Money (Togo)", placeholder: "Numéro T-Money" },
  { value: "moov_togo", label: "Moov (Togo)", placeholder: "Numéro Moov" },
  { value: "mtn_rwanda", label: "MTN Money (Rwanda)", placeholder: "Numéro MTN" },
  { value: "tigo_rwanda", label: "Tigo Cash (Rwanda)", placeholder: "Numéro Tigo" },
  { value: "airtel_rwanda", label: "Airtel (Rwanda)", placeholder: "Numéro Airtel" },
  { value: "paypal", label: "PayPal", placeholder: "Email PayPal" },
  { value: "wero", label: "Wero (UE)", placeholder: "Téléphone ou email Wero" },
];

export default function Parrainage() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [lastCountry, setLastCountry] = useState("Gabon");
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [missions, setMissions] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [league, setLeague] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);

  useEffect(() => {
    // Always fetch the live referral settings, even when logged out, so the
    // discount/message shown here stays in sync with whatever the admin last set.
    api.get("/referral-config-public").then(({ data }) => setPublicConfig(data)).catch(() => {});
  }, []);

  const refresh = async () => {
    if (!user) return;
    try {
      const [c, s, p, m, l] = await Promise.all([
        api.get("/me/referral-code"),
        api.get("/me/referral-stats"),
        api.get("/me/referral-payouts").catch(() => ({ data: [] })),
        api.get("/me/missions").catch(() => ({ data: null })),
        api.get("/me/league").catch(() => ({ data: null })),
      ]);
      if (c.data?.code) setCode(c.data.code);
      setStats(s.data);
      setPayouts(p.data || []);
      setMissions(m.data || null);
      setLeague(l.data || null);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      const codes = getLocalTxCodes();
      if (codes.length) {
        try {
          const { data } = await api.get(`/transactions/track/${codes[0]}`);
          if (data?.corridor === "ga_fr") setLastCountry("France");
          else if (data?.corridor === "ga_rw") setLastCountry("Rwanda");
          else if (data?.corridor === "tg_ga") setLastCountry("Gabon");
        } catch (_e) { /* ignore */ }
      }
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const link = `https://swiftlink-africa.com/register?ref=${encodeURIComponent(code)}`;
  const programs = [
    {
      key: "ga_fr",
      flagFrom: "🇬🇦",
      flagTo: "🇫🇷",
      title: "Gabon → France",
      discount: stats?.discount_by_corridor?.ga_fr ?? publicConfig?.discount_percent ?? 5,
      color: "from-blue-500/15 to-blue-600/10 border-blue-200",
      accent: "text-blue-700",
      sendPath: "/envoyer?corridor=ga_fr",
    },
    {
      key: "ga_rw",
      flagFrom: "🇬🇦",
      flagTo: "🇷🇼",
      title: "Gabon → Rwanda",
      discount: stats?.discount_by_corridor?.ga_rw ?? publicConfig?.discount_percent ?? 5,
      color: "from-teal-500/15 to-teal-600/10 border-teal-200",
      accent: "text-teal-700",
      sendPath: "/envoyer?corridor=ga_rw",
    },
  ];
  const discountPercent = stats?.discount_percent ?? publicConfig?.discount_percent ?? 5;
  const tierThresholdXaf = stats?.tier_threshold_xaf ?? publicConfig?.tier_threshold_xaf ?? 200000;
  const tierCommissionEur = stats?.tier_commission_eur ?? publicConfig?.tier_commission_eur ?? 5;
  const messageTemplate =
    stats?.message_template ||
    "Utilisez le code {code} pour profiter de {reduction} % de réduction sur vos frais de transfert Gabon → France ou Gabon → Rwanda. Lien : {lien}";
  const pitchLine = messageTemplate
    .replace(/\{code\}/g, code)
    .replace(/\{lien\}/g, link)
    .replace(/\{reduction\}/g, String(discountPercent));
  const message =
    `Découvrez Swiftlink-Africa ! Je l'utilise pour envoyer de l'argent au ${lastCountry}.\n\n` +
    `Inscrivez-vous ici : ${link}\n\n` +
    pitchLine;

  const copy = async (text, what = "Lien") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${what} copié`);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { toast.error("Impossible de copier"); }
  };

  // Record the daily share reward server-side (server grants ≤ 1 share/day).
  const recordShareReward = async () => {
    if (!user || sharing) return;
    setSharing(true);
    try {
      const { data } = await api.post("/me/missions/share");
      if (data?.awarded) {
        toast.success(`Mission accomplie : +${Number(data.amount_eur).toFixed(2)} € dans votre cagnotte !`);
      } else if (data?.reason === "duplicate" || data?.reason === "daily_cap_reached") {
        toast.message("Partage déjà validé aujourd'hui.");
      }
      await refresh();
    } catch (_e) { /* non-blocking */ } finally {
      setSharing(false);
    }
  };

  const shareSwiftlink = async () => {
    let shared = false;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Swiftlink-Africa",
          text: message,
          url: link,
        });
        shared = true;
      } catch (_e) { /* user cancelled */ shared = false; }
    } else {
      // Desktop fallback: open WhatsApp share
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
      shared = true;
    }
    if (shared) await recordShareReward();
  };

  return (
    <div className="min-h-screen sl-hero-bg pt-24 sm:pt-28 pb-24">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--sl-secondary)]/15 text-[var(--sl-secondary-dark)] mb-6"><Gift size={28} /></div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Parrainez, gagnez.</h1>
          <p className="mt-4 text-gray-600 max-w-xl mx-auto text-sm sm:text-base text-justify sm:text-center" style={{ hyphens: "auto" }}>
            Partagez votre code à vos proches : ils bénéficient de <span className="font-semibold text-emerald-700">{discountPercent} % de remise sur les frais</span> de transfert (Gabon → France et Gabon → Rwanda). Le volume de leurs transferts est cumulé : dès <span className="font-semibold">{tierThresholdXaf.toLocaleString("fr-FR")} FCFA</span> de cumul, vous débloquez <span className="font-semibold text-emerald-700">{tierCommissionEur} €</span> de récompense.
          </p>
          <div className="mt-6">
            {user ? (
              <>
                <button
                  type="button"
                  onClick={shareSwiftlink}
                  className="sl-btn-primary text-base px-6 py-3 shadow-lg shadow-[var(--sl-primary)]/20 hover:shadow-xl transition-shadow"
                  data-testid="share-swiftlink-btn"
                >
                  <Share2 size={18} /> Partager Swiftlink
                </button>
                <p className="mt-2 text-xs text-gray-500">Votre code <span className="font-mono font-semibold text-[var(--sl-primary)]">{code}</span> est inclus automatiquement.</p>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3" data-testid="referral-login-gate">
                <div className="flex gap-3">
                  <Link to="/login" className="sl-btn-primary text-base px-6 py-3" data-testid="referral-login-btn">
                    Se connecter
                  </Link>
                  <Link to="/register" className="sl-btn-outline text-base px-6 py-3" data-testid="referral-register-btn">
                    Créer un compte
                  </Link>
                </div>
                <p className="text-xs text-gray-500 max-w-sm">
                  Seuls les membres inscrits sur Swiftlink-Africa reçoivent un code de parrainage personnel.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Daily paid missions — ambassador program */}
        {user && missions && missions.enabled && <MissionsSection missions={missions} onShare={shareSwiftlink} sharing={sharing} />}

        {/* Monthly referral leagues — Bronze/Argent/Or */}
        {user && league && <LeagueSection league={league} link={link} />}

        {/* Two distinct referral programs — Gabon (FR) and Rwanda */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {programs.map((p) => (
            <div
              key={p.key}
              className={`relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br ${p.color} p-5`}
              data-testid={`program-card-${p.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-2xl">{p.flagFrom} → {p.flagTo}</div>
                <span className={`text-xs font-bold uppercase tracking-widest ${p.accent}`}>Programme</span>
              </div>
              <h3 className="font-heading text-xl font-bold mt-3">{p.title}</h3>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`font-mono font-bold text-base ${p.accent}`}>−{p.discount} %</span>
                  <span className="text-gray-700">de remise sur les frais pour votre filleul</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-emerald-700 font-semibold">+</span>
                  Le volume des transferts s&apos;ajoute au cumul (peu importe le montant)
                </div>
              </div>
              <Link to={p.sendPath} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "var(--sl-primary)" }} data-testid={`program-link-${p.key}`}>
                Voir le corridor <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>

        {/* Stats banner — only when logged-in */}
        {user && stats && (
          <>
            <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-3" data-testid="referral-stats">
              <StatCard
                icon={Users}
                label="Filleuls"
                value={String(stats.filleuls || 0)}
                hint={stats.filleuls === 1 ? "personne" : "personnes"}
              />
              <StatCard
                icon={TrendingUp}
                label="Transactions"
                value={String(stats.tx_count || 0)}
                hint={stats.tx_count > 1 ? "transferts" : "transfert"}
                accent
              />
              <StatCard
                icon={Wallet}
                label="Gains"
                value={formatAmount(stats.gains_total_xaf || 0, "XAF")}
                hint={`≈ ${(stats.gains_total_eur || 0).toFixed(2)} €`}
                primary
              />
            </div>

            {/* Volume-cumul progress gauge */}
            <CumulGauge stats={stats} />

            {/* Tier-unlock history */}
            {(stats.tier_history || []).length > 0 && (
              <div className="sl-card mt-4" data-testid="referral-tier-history">
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3">Récompenses débloquées</div>
                <ul className="divide-y divide-gray-100">
                  {(stats.tier_history || []).map((t) => (
                    <li key={t.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`tier-${t.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center"><TrendingUp size={13} /></span>
                        <div>
                          <div className="font-semibold">+{Number(t.commission_eur).toFixed(2)} €</div>
                          <div className="text-xs text-gray-500">{new Date(t.unlocked_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">Palier {Number(t.threshold_xaf).toLocaleString("fr-FR")} FCFA</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Balance + withdraw card */}
            <div className="sl-card mt-4 sm:mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="referral-balance-card">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Solde disponible</div>
                <div className="font-heading text-2xl sm:text-3xl font-bold text-emerald-700 mt-1" data-testid="referral-balance">
                  {formatAmount(stats.gains_available_xaf || 0, "XAF")}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {(stats.gains_paid_xaf || 0) > 0 && <span>Payé : {formatAmount(stats.gains_paid_xaf, "XAF")} · </span>}
                  {(stats.gains_pending_xaf || 0) > 0 && <span>En attente : {formatAmount(stats.gains_pending_xaf, "XAF")} · </span>}
                  Minimum {formatAmount(stats.payout_min_xaf, "XAF")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPayoutModal(true)}
                disabled={(stats.gains_available_xaf || 0) < (stats.payout_min_xaf || 0)}
                className="sl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="referral-payout-btn"
              >
                <Banknote size={16} /> Demander un virement
              </button>
            </div>

            {/* Payout history */}
            {payouts.length > 0 && (
              <div className="sl-card mt-4" data-testid="referral-payouts-history">
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3">Mes demandes</div>
                <ul className="divide-y divide-gray-100">
                  {payouts.map((p) => (
                    <PayoutRow key={p.id} p={p} />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {user ? (
          <div className="sl-card mt-6 sm:mt-8" data-testid="referral-card">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Votre code de parrainage</div>
              {user && (
                <button
                  type="button"
                  onClick={() => setShowCodeModal(true)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sl-primary)] hover:underline"
                  data-testid="referral-customize-btn"
                >
                  <Pencil size={12} /> Personnaliser
                </button>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2 bg-[var(--sl-bg-alt)] rounded-xl px-4 py-3">
              <span className="font-mono text-lg font-bold tracking-wider text-[var(--sl-primary)] flex-1 truncate" data-testid="referral-code">{code}</span>
              <button onClick={() => copy(code, "Code")} className="text-gray-400 hover:text-[var(--sl-primary)] p-1" data-testid="referral-copy-code"><Copy size={14} /></button>
            </div>

            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mt-5">Lien de parrainage</div>
            <div className="mt-3 flex flex-wrap items-center gap-3 bg-[var(--sl-bg-alt)] rounded-xl px-4 py-3">
              <span className="flex-1 font-mono text-xs sm:text-sm break-all" data-testid="referral-link">{link}</span>
              <button
                onClick={() => copy(link, "Lien")}
                className={`inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-3 py-1.5 transition ${copied ? "bg-emerald-100 text-emerald-700" : "bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] hover:bg-[var(--sl-primary)]/20"}`}
                data-testid="referral-copy"
              ><Copy size={13} /> {copied ? "Copié" : "Copier"}</button>
            </div>

            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mt-5">Message à partager</div>
            <pre className="mt-2 whitespace-pre-wrap rounded-xl bg-[var(--sl-bg-alt)] px-4 py-3 text-xs text-gray-700 leading-relaxed font-sans">{message}</pre>

            <div className="mt-4 flex flex-wrap gap-3">
              <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer" className="sl-btn-primary" data-testid="referral-whatsapp"><Share2 size={16} /> Partager sur WhatsApp</a>
              <a href={`mailto:?subject=${encodeURIComponent("Découvre Swiftlink-Africa")}&body=${encodeURIComponent(message)}`} className="sl-btn-outline" data-testid="referral-email">Partager par email</a>
              <button onClick={() => copy(message, "Message")} className="sl-btn-outline"><Copy size={14} /> Copier</button>
            </div>
          </div>
        ) : (
          <div className="sl-card mt-6 sm:mt-8 text-center" data-testid="referral-logged-out-card">
            <Lock size={24} className="mx-auto text-gray-300" />
            <h3 className="font-heading text-lg font-bold mt-3">Connectez-vous pour parrainer</h3>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
              Pour garantir un programme sans fraude, seul un membre inscrit sur Swiftlink-Africa possède un code de parrainage. Créez votre compte pour obtenir le vôtre.
            </p>
            <div className="flex justify-center gap-3 mt-5">
              <Link to="/login" className="sl-btn-primary" data-testid="referral-logged-out-login">Se connecter</Link>
              <Link to="/register" className="sl-btn-outline" data-testid="referral-logged-out-register">Créer un compte</Link>
            </div>
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          <Step icon={Share2} title="1. Partagez" desc="Envoyez votre code ou votre lien à vos proches." />
          <Step icon={Users} title="2. Ils s'inscrivent" desc="Votre filleul effectue son transfert Gabon → France ou Gabon → Rwanda avec votre code." />
          <Step icon={Wallet} title="3. Tout le monde gagne" desc={`Votre filleul économise ${discountPercent} % sur ses frais. Vous gagnez ${tierCommissionEur} € dès ${tierThresholdXaf.toLocaleString("fr-FR")} FCFA de volume cumulé.`} />
        </div>

        <div className="text-center mt-10">
          <Link to="/envoyer" className="sl-btn-outline" data-testid="referral-cta-send">Effectuer un transfert <ArrowRight size={14} /></Link>
        </div>
      </div>

      {showPayoutModal && stats && (
        <PayoutModal
          available={stats.gains_available_xaf || 0}
          min={stats.payout_min_xaf || 5000}
          onClose={() => setShowPayoutModal(false)}
          onCreated={() => { setShowPayoutModal(false); refresh(); }}
        />
      )}

      {showCodeModal && (
        <CustomCodeModal
          currentCode={code}
          onClose={() => setShowCodeModal(false)}
          onUpdated={(newCode) => { setCode(newCode); setShowCodeModal(false); }}
        />
      )}
    </div>
  );
}

function CustomCodeModal({ currentCode, onClose, onUpdated }) {
  const [val, setVal] = useState(currentCode || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const clean = (val || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length < 4 || clean.length > 20) {
      toast.error("Le code doit faire entre 4 et 20 caractères (lettres et chiffres).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/me/referral-code", { code: clean });
      toast.success("Votre nouveau code de parrainage est actif !");
      onUpdated?.(data.code);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="custom-code-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Personnaliser mon code</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Fermer" data-testid="custom-code-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Choisissez un code mémorable pour vos parrainages. 4 à 20 caractères, lettres et chiffres uniquement.
        </p>
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          className="sl-input font-mono text-lg tracking-wider uppercase"
          placeholder="EX. MARIE2026"
          maxLength={20}
          data-testid="custom-code-input"
        />
        <div className="text-xs text-gray-500 mt-1.5">
          Caractères : <span className="font-semibold">{val.length}</span> / 20
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="custom-code-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={saving} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="custom-code-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, primary, accent }) {
  const valueClass = primary
    ? "text-[var(--sl-primary)]"
    : accent
    ? "text-[var(--sl-secondary-dark)]"
    : "text-gray-900";
  return (
    <div className="sl-card !p-3 sm:!p-5 text-center sm:text-left">
      <div className="flex items-center gap-1.5 sm:gap-2 justify-center sm:justify-start">
        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] grid place-items-center"><Icon size={12} /></span>
        <span className="text-[9px] sm:text-xs uppercase tracking-widest text-gray-500 font-semibold">{label}</span>
      </div>
      <div className={`font-heading text-base sm:text-2xl font-bold mt-1 sm:mt-2 ${valueClass} truncate`}>{value}</div>
      <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 truncate">{hint}</div>
    </div>
  );
}

function Step({ icon: Icon, title, desc }) {
  return (
    <div className="sl-card sl-card-hoverable">
      <div className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] grid place-items-center mb-3"><Icon size={18} /></div>
      <div className="font-heading font-semibold">{title}</div>
      <div className="text-sm text-gray-600 mt-1">{desc}</div>
    </div>
  );
}

function PayoutRow({ p }) {
  const channel = PAYOUT_CHANNELS.find((c) => c.value === p.channel)?.label || p.channel;
  const statusMap = {
    pending: { icon: Clock3, label: "En attente", color: "text-amber-700 bg-amber-50" },
    approved: { icon: CheckCircle2, label: "Approuvée", color: "text-blue-700 bg-blue-50" },
    paid: { icon: CheckCircle2, label: "Payée", color: "text-emerald-700 bg-emerald-50" },
    rejected: { icon: XCircle, label: "Refusée", color: "text-red-700 bg-red-50" },
  };
  const st = statusMap[p.status] || statusMap.pending;
  const Ic = st.icon;
  return (
    <li className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`payout-row-${p.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{formatAmount(p.amount_xaf, "XAF")} <span className="text-gray-400 font-normal">· {channel}</span></div>
        <div className="text-xs text-gray-500 truncate">{p.holder_name} · {p.account}</div>
      </div>
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${st.color}`}>
        <Ic size={11} /> {st.label}
      </span>
    </li>
  );
}

function PayoutModal({ available, min, onClose, onCreated }) {
  const [amount, setAmount] = useState(String(Math.min(available, Math.max(min, 5000))));
  const [channel, setChannel] = useState(PAYOUT_CHANNELS[0].value);
  const [account, setAccount] = useState("");
  const [holderName, setHolderName] = useState("");
  const [saving, setSaving] = useState(false);

  const placeholder = PAYOUT_CHANNELS.find((c) => c.value === channel)?.placeholder || "Compte de réception";

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt < min) { toast.error(`Minimum : ${formatAmount(min, "XAF")}`); return; }
    if (amt > available + 0.5) { toast.error(`Solde insuffisant. Disponible : ${formatAmount(available, "XAF")}`); return; }
    if (!holderName.trim()) { toast.error("Indiquez le nom du bénéficiaire"); return; }
    if (!account.trim()) { toast.error("Indiquez le compte de réception"); return; }
    setSaving(true);
    try {
      await api.post("/me/referral-payouts", {
        amount_xaf: amt,
        channel,
        account: account.trim(),
        holder_name: holderName.trim(),
      });
      toast.success("Demande envoyée. Vous serez notifié dès traitement.");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="payout-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Demande de virement</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Fermer" data-testid="payout-close">
            <X size={18} />
          </button>
        </div>

        <div className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs p-3 mb-4">
          Solde disponible : <span className="font-bold">{formatAmount(available, "XAF")}</span>. Minimum : {formatAmount(min, "XAF")}.
        </div>

        <div className="space-y-3">
          <div>
            <label className="sl-label">Montant à recevoir (FCFA)</label>
            <input type="number" min={min} max={available} value={amount} onChange={(e) => setAmount(e.target.value)} className="sl-input" data-testid="payout-amount" />
          </div>
          <div>
            <label className="sl-label">Canal de paiement</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="sl-input" data-testid="payout-channel">
              {PAYOUT_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="sl-label">Nom du bénéficiaire</label>
            <input type="text" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Ex. Jean Dupont" className="sl-input" data-testid="payout-name" />
          </div>
          <div>
            <label className="sl-label">Compte de réception</label>
            <input type="text" value={account} onChange={(e) => setAccount(e.target.value)} placeholder={placeholder} className="sl-input" data-testid="payout-account" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="payout-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={saving} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="payout-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />} Envoyer la demande
          </button>
        </div>
      </div>
    </div>
  );
}

const MISSION_META = {
  share: { icon: Share2, color: "text-sky-700", bg: "bg-sky-100" },
  signup: { icon: UserPlus, color: "text-violet-700", bg: "bg-violet-100" },
  first_transaction: { icon: SendIcon, color: "text-emerald-700", bg: "bg-emerald-100" },
  qualified_referral: { icon: BadgeCheck, color: "text-amber-700", bg: "bg-amber-100" },
};

function MissionsSection({ missions, onShare, sharing }) {
  const w = missions.wallet || {};
  const cap = Number(missions.daily_cap_eur || 1);
  const used = Number(missions.used_today_eur || 0);
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const [historyOpen, setHistoryOpen] = useState(false);
  const fullHistory = missions.history || [];
  const HISTORY_PREVIEW = 5;
  const visibleHistory = historyOpen ? fullHistory : fullHistory.slice(0, HISTORY_PREVIEW);
  return (
    <div className="mt-8" data-testid="missions-section">
      {/* Rewards wallet (cagnotte missions) — distinct from transfer funds */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--sl-primary)] to-[var(--sl-primary-dark,#0a3d2e)] text-white p-5 sm:p-6 shadow-lg" data-testid="missions-wallet-card">
        <div className="absolute -right-6 -top-6 opacity-20"><Sparkles size={110} /></div>
        <div className="flex items-start justify-between gap-3 relative">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 font-semibold">
              <Target size={14} /> Missions quotidiennes
            </div>
            <div className="mt-2 text-xs text-white/70">Cagnotte récompenses</div>
            <div className="font-heading text-3xl sm:text-4xl font-bold mt-0.5" data-testid="missions-wallet-balance">
              {Number(w.balance_eur || 0).toFixed(2)} €
            </div>
            <div className="text-xs text-white/70 mt-1">
              Cumul total gagné : <span className="font-semibold text-white">{Number(w.lifetime_eur || 0).toFixed(2)} €</span>
            </div>
            {Number(w.pending_eur || 0) > 0 && (
              <div className="text-xs text-white/70 mt-0.5" data-testid="missions-wallet-pending">
                En attente de déblocage : <span className="font-semibold text-white">{Number(w.pending_eur || 0).toFixed(2)} €</span>
              </div>
            )}
          </div>
        </div>
        {/* Daily cap progress */}
        <div className="mt-4 relative">
          <div className="flex items-center justify-between text-[11px] text-white/80 mb-1.5">
            <span>Gains du jour : <span className="font-bold text-white" data-testid="missions-used-today">{used.toFixed(2)} €</span></span>
            <span>Plafond {cap.toFixed(2)} €/jour</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/20 overflow-hidden" data-testid="missions-cap-bar">
            <div className="h-full bg-[var(--sl-secondary)] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          {missions.cap_reached && (
            <div className="mt-2 text-[11px] text-white/90 inline-flex items-center gap-1" data-testid="missions-cap-reached">
              <Check size={12} /> Plafond du jour atteint — revenez demain pour gagner plus !
            </div>
          )}
        </div>
      </div>

      {/* Unlock conditions — cagnotte stays pending until ALL missions done + account verified */}
      <div className={`sl-card mt-3 ${missions.unlocked_today ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/50"}`} data-testid="missions-unlock-card">
        {missions.unlocked_today ? (
          <div className="flex items-center gap-2 text-emerald-800 text-sm font-medium">
            <CheckCircle2 size={16} /> Cagnotte du jour débloquée — vos gains sont disponibles !
          </div>
        ) : (
          <>
            <div className="text-sm font-semibold text-amber-900 mb-2">Déblocage de la cagnotte</div>
            <p className="text-xs text-amber-800/80 mb-3">Vos gains restent en attente jusqu'à ce que ces deux conditions soient remplies :</p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-center gap-2" data-testid="unlock-cond-missions">
                {missions.all_missions_done_today ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Lock size={14} className="text-amber-500" />}
                <span className={missions.all_missions_done_today ? "text-emerald-700" : "text-gray-700"}>Toutes les missions du jour validées</span>
              </li>
              <li className="flex items-center gap-2" data-testid="unlock-cond-verified">
                {missions.account_verified ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Lock size={14} className="text-amber-500" />}
                <span className={missions.account_verified ? "text-emerald-700" : "text-gray-700"}>Compte vérifié (pièce d'identité)</span>
              </li>
            </ul>
            {!missions.account_verified && (
              <Link to="/compte" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--sl-primary)] hover:underline" data-testid="unlock-verify-cta">
                <BadgeCheck size={14} /> Vérifier mon compte <ArrowRight size={12} />
              </Link>
            )}
          </>
        )}
      </div>

      {/* Mission list */}
      <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="missions-list">
        {(missions.missions || []).map((m) => {
          const meta = MISSION_META[m.type] || MISSION_META.share;
          const Icon = meta.icon;
          return (
            <div
              key={m.type}
              className={`sl-card !p-4 flex items-start gap-3 ${m.done_today ? "ring-1 ring-emerald-200" : ""}`}
              data-testid={`mission-card-${m.type}`}
            >
              <span className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center ${meta.bg} ${meta.color}`}><Icon size={18} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-heading font-semibold text-sm truncate">{m.label}</h4>
                  <span className="shrink-0 text-xs font-bold text-emerald-700" data-testid={`mission-reward-${m.type}`}>+{Number(m.reward_eur).toFixed(2)} €</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5 leading-snug">{m.description}</p>
                <div className="mt-2 flex items-center gap-2">
                  {m.done_today ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid={`mission-done-${m.type}`}>
                      <CheckCircle2 size={11} /> Validée aujourd'hui{m.count_today > 1 ? ` ×${m.count_today}` : ""}
                    </span>
                  ) : m.type === "share" ? (
                    <button
                      type="button"
                      onClick={onShare}
                      disabled={sharing}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[var(--sl-primary)] rounded-full px-3 py-1 hover:opacity-90 disabled:opacity-60"
                      data-testid="mission-share-action"
                    >
                      {sharing ? <Loader2 size={11} className="animate-spin" /> : <Share2 size={11} />} Partager maintenant
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
                      <Lock size={10} /> Automatique
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* History */}
      {fullHistory.length > 0 && (
        <div className="sl-card mt-4" data-testid="missions-history">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-semibold">
              <History size={14} /> Historique des gains
            </div>
            {fullHistory.length > HISTORY_PREVIEW && (
              <button
                type="button"
                onClick={() => setHistoryOpen((o) => !o)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sl-primary)] hover:underline"
                data-testid="missions-history-toggle"
              >
                {historyOpen ? "Réduire" : `Voir tout (${fullHistory.length})`}
                <ArrowRight size={11} className={`transition-transform ${historyOpen ? "-rotate-90" : "rotate-90"}`} />
              </button>
            )}
          </div>
          <ul className="divide-y divide-gray-100">
            {visibleHistory.map((h) => {
              const meta = MISSION_META[h.mission_type] || MISSION_META.share;
              const Icon = meta.icon;
              const cancelled = h.status === "cancelled";
              return (
                <li key={h.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`mission-entry-${h.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center ${meta.bg} ${meta.color}`}><Icon size={13} /></span>
                    <div className="min-w-0">
                      <div className={`font-medium truncate ${cancelled ? "line-through text-gray-400" : ""}`}>{h.mission_label}</div>
                      <div className="text-xs text-gray-500">{new Date(h.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} · {new Date(h.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                  <span className={`shrink-0 font-semibold ${cancelled ? "text-gray-400" : "text-emerald-700"}`}>
                    {cancelled ? "Annulé" : `+${Number(h.amount_eur).toFixed(2)} €`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className="mt-3 text-[11px] text-gray-500 text-center" data-testid="missions-disclaimer">
        Les gains sont calculés et validés automatiquement par Swiftlink. Cette cagnotte est distincte de vos fonds de transfert. Plafond de {cap.toFixed(2)} € par jour.
      </p>
    </div>
  );
}


const LEAGUE_STATUS_META = {
  pending_validation: { label: "En revue", color: "text-amber-700 bg-amber-50" },
  validated: { label: "Validée", color: "text-blue-700 bg-blue-50" },
  paid: { label: "Payée", color: "text-emerald-700 bg-emerald-50" },
  rejected: { label: "Refusée", color: "text-red-700 bg-red-50" },
};
const LEAGUE_TIER_META = {
  bronze: { color: "text-amber-700 bg-amber-50 border-amber-200", bar: "from-amber-400 to-amber-600" },
  argent: { color: "text-slate-600 bg-slate-100 border-slate-300", bar: "from-slate-400 to-slate-500" },
  or: { color: "text-yellow-700 bg-yellow-50 border-yellow-300", bar: "from-yellow-400 to-yellow-600" },
};

function useCountdown(targetIso) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(targetIso).getTime() - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, new Date(targetIso).getTime() - Date.now())), 60000);
    return () => clearInterval(id);
  }, [targetIso]);
  const days = Math.floor(left / 86400000);
  const hours = Math.floor((left % 86400000) / 3600000);
  return { days, hours };
}

function LeagueSection({ league, link }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const count = league.filleuls_count || 0;
  const nextThreshold = league.next_tier_threshold;
  const pct = nextThreshold ? Math.min(100, Math.round((count / nextThreshold) * 100)) : 100;
  const history = league.history || [];
  const HISTORY_PREVIEW = 3;
  const visibleHistory = historyOpen ? history : history.slice(0, HISTORY_PREVIEW);
  const activeMeta = LEAGUE_TIER_META[league.current_tier] || { color: "text-gray-500 bg-gray-50 border-gray-200", bar: "from-[var(--sl-primary)] to-[var(--sl-primary)]" };
  const monthLabel = (() => {
    try { return new Date(`${league.month}-01T00:00:00Z`).toLocaleDateString("fr-FR", { month: "long", year: "numeric", timeZone: "UTC" }); }
    catch (_e) { return league.month; }
  })();
  const monthEndIso = (() => {
    const [y, m] = league.month.split("-").map(Number);
    return new Date(Date.UTC(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 1)).toISOString();
  })();
  const { days: daysLeft, hours: hoursLeft } = useCountdown(monthEndIso);

  const shareLeague = async () => {
    const text = league.current_tier
      ? `🏆 Je viens d'atteindre la Ligue ${league.current_tier_label} ${league.current_tier_emoji} sur Swiftlink-Africa en parrainant ${count} filleul${count !== 1 ? "s" : ""} validé${count !== 1 ? "s" : ""} ce mois-ci ! Inscris-toi avec mon lien pour tes transferts d'argent : ${link}`
      : `💪 Je parraine sur Swiftlink-Africa et je vise la Ligue ${league.next_tier_label || "Bronze"} ${league.next_tier_emoji || "🥉"} ce mois-ci ! Inscris-toi avec mon lien et aide-moi à y arriver : ${link}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Ma ligue Swiftlink-Africa", text }); return; } catch (_e) { /* cancelled, fall through */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="sl-card mt-6 border-2 border-amber-200/60 bg-gradient-to-br from-amber-50/50 to-transparent" data-testid="league-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-amber-600" />
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Ligue de parrainage — {monthLabel}</div>
        </div>
        <Link to="/mes-filleuls" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sl-primary)] hover:underline" data-testid="league-filleuls-link">
          <Users size={12} /> Mes filleuls <ArrowRight size={11} />
        </Link>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-bold ${activeMeta.color}`} data-testid="league-current-badge">
          {league.current_tier ? <>{league.current_tier_emoji} Ligue {league.current_tier_label}</> : "Aucune ligue"}
        </span>
        <div className="text-sm text-gray-700 leading-relaxed">
          <span className="font-bold text-lg" data-testid="league-count">{count}</span> filleul{count !== 1 ? "s" : ""} validé{count !== 1 ? "s" : ""} ce mois-ci
        </div>
        <span
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-800"
          data-testid="league-countdown"
          title="Temps restant pour valider des filleuls ce mois-ci"
        >
          <Clock3 size={12} /> {daysLeft}j {hoursLeft}h restantes
        </span>
      </div>

      <button
        type="button"
        onClick={shareLeague}
        className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-semibold px-4 py-2.5 hover:brightness-110 transition"
        data-testid="league-share-whatsapp"
      >
        <Share2 size={14} /> Partager ma ligue sur WhatsApp
      </button>

      {league.next_tier ? (
        <>
          <div className="relative mt-5 h-3 rounded-full bg-gray-100 overflow-hidden" data-testid="league-progress">
            <div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${activeMeta.bar} transition-all duration-700`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-xs text-gray-500">{count} / {nextThreshold} filleuls</div>

          <div className="mt-4 rounded-xl bg-white/70 border border-amber-100 px-4 py-4" data-testid="league-progress-text">
            <p className="text-sm text-gray-700 leading-relaxed">
              Vous avez parrainé <span className="font-semibold">{count}</span> filleul{count !== 1 ? "s" : ""} sur {nextThreshold}.
            </p>
            <p className="mt-2 text-base leading-relaxed">
              Il vous manque{" "}
              <span className="font-bold text-amber-700 text-lg">{league.filleuls_needed_for_next}</span>{" "}
              filleul{league.filleuls_needed_for_next !== 1 ? "s" : ""} pour débloquer votre récompense de{" "}
              <span className="font-bold text-emerald-700">{Number(league.next_tier_reward_xaf).toLocaleString("fr-FR")} FCFA</span>.
            </p>
            <p className="mt-1.5 text-xs text-gray-500">
              {league.next_tier_emoji} Ligue {league.next_tier_label} — à atteindre avant la fin du mois.
            </p>
          </div>
        </>
      ) : (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-4" data-testid="league-max-reached">
          <p className="text-sm font-medium text-emerald-700 leading-relaxed">🏆 Ligue Or atteinte — la récompense maximale de ce mois !</p>
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500 border-t border-amber-100 pt-3 leading-relaxed">
        <span className="flex items-center gap-1.5"><Banknote size={12} /> Versement prévu le {new Date(`${league.next_payment_date}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", timeZone: "UTC" })}</span>
        <span>Seule la récompense de la ligue la plus haute atteinte est versée — à reconquérir chaque mois.</span>
      </div>

      {history.length > 0 && (
        <div className="mt-4 pt-3 border-t border-amber-100">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Historique des ligues</div>
            {history.length > HISTORY_PREVIEW && (
              <button type="button" onClick={() => setHistoryOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sl-primary)] hover:underline" data-testid="league-history-toggle">
                {historyOpen ? "Réduire" : `Voir tout (${history.length})`}
                <ArrowRight size={11} className={`transition-transform ${historyOpen ? "-rotate-90" : "rotate-90"}`} />
              </button>
            )}
          </div>
          <ul className="divide-y divide-amber-100/70">
            {visibleHistory.map((h) => {
              const st = LEAGUE_STATUS_META[h.status] || LEAGUE_STATUS_META.pending_validation;
              return (
                <li key={h.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`league-history-${h.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg leading-none">{h.tier_emoji}</span>
                    <div>
                      <div className="font-medium">{monthLabelFor(h.month)} · Ligue {h.tier_label}</div>
                      <div className="text-xs text-gray-500">{h.filleuls_count} filleuls validés</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald-700">{Number(h.reward_xaf).toLocaleString("fr-FR")} FCFA</div>
                    <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function monthLabelFor(ym) {
  try { return new Date(`${ym}-01T00:00:00Z`).toLocaleDateString("fr-FR", { month: "short", year: "numeric", timeZone: "UTC" }); }
  catch (_e) { return ym; }
}

function CumulGauge({ stats }) {
  const threshold = Number(stats.tier_threshold_xaf || 200000);
  const pending = Math.max(0, Number(stats.volume_pending_xaf || 0));
  const pct = Math.min(100, Math.round((pending / threshold) * 100));
  const remaining = Math.max(0, threshold - pending);
  const commission = Number(stats.tier_commission_eur || 5);
  return (
    <div className="sl-card mt-4 sm:mt-5" data-testid="referral-cumul-gauge">
      <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Cumul des transferts de vos filleuls</div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <div className="font-heading text-xl sm:text-3xl font-bold truncate" data-testid="cumul-value">
            {pending.toLocaleString("fr-FR")} <span className="text-xs sm:text-base font-mono text-gray-400">FCFA</span>
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1 leading-snug">
            sur {threshold.toLocaleString("fr-FR")} FCFA<br className="sm:hidden" /> <span className="hidden sm:inline">· </span>récompense <span className="font-semibold text-emerald-700">+{commission} €</span>
          </div>
        </div>
        <div className="text-right min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 font-semibold">Reste avant récompense</div>
          <div className="font-heading text-lg sm:text-xl font-bold text-[var(--sl-primary)] mt-1 truncate" data-testid="cumul-remaining">
            {remaining.toLocaleString("fr-FR")} FCFA
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 mt-1 leading-snug" data-testid="cumul-unlocked-count">
            {stats.tiers_unlocked || 0} récompense{(stats.tiers_unlocked || 0) > 1 ? "s" : ""}<br className="sm:hidden" /> débloquée{(stats.tiers_unlocked || 0) > 1 ? "s" : ""}
          </div>
        </div>
      </div>
      <div className="relative mt-4 h-4 rounded-full bg-gray-100 overflow-hidden" aria-label="Progression du cumul" data-testid="cumul-progress">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-700 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
        <div className="absolute inset-0 grid place-items-center text-[10px] font-bold text-white mix-blend-difference">
          {pct} %
        </div>
      </div>
      <div className="mt-3 text-xs text-gray-500" data-testid="cumul-help">
        Chaque transfert effectué par un filleul (peu importe le montant) augmente votre cumul. Dès {threshold.toLocaleString("fr-FR")} FCFA atteints, {commission} € sont automatiquement crédités et le compteur reprend depuis le surplus.
      </div>
    </div>
  );
}